import { useState, useEffect, lazy, Suspense } from "react";
import {
  Save,
  Database,
  Building2,
  CreditCard,
  Bell,
  BarChart3,
  Shield,
  Server,
  Share2,
  Loader2,
  Bot,
  Banknote,
  Megaphone,
} from "lucide-react";
import type { Settings, SettingsCategory } from "../components/settings/types";
import { api } from "@/api/client";

const CompanyTab = lazy(() => import("../components/settings/CompanyTab"));
const SmtpTab = lazy(() => import("../components/settings/SmtpTab"));
const NotificationsTab = lazy(() => import("../components/settings/NotificationsTab"));
const BookingTab = lazy(() => import("../components/settings/BookingTab"));
const SeoTab = lazy(() => import("../components/settings/SeoTab"));
const SocialTab = lazy(() => import("../components/settings/SocialTab"));
const SecurityTab = lazy(() => import("../components/settings/SecurityTab"));
const AiTab = lazy(() => import("../components/settings/AiTab"));
const MarketingTab = lazy(() => import("../components/settings/MarketingTab"));
const CacheTab = lazy(() => import("../components/settings/CacheTab"));
const FinanceTab = lazy(() => import("../components/settings/FinanceTab"));

// Tabs that manage their own state via dedicated endpoints (no settings-category save).
type SelfManagedTab = "cache" | "finance";
const SELF_MANAGED: SelfManagedTab[] = ["cache", "finance"];

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsCategory | SelfManagedTab>("company");
  const isSelfManaged = SELF_MANAGED.includes(activeTab as SelfManagedTab);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const result = await api.get<{ data: Settings }>("/api/admin/settings");
      setSettings(result.data);
    } catch {
      setMessage({ type: "error", text: "Failed to load settings" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings || isSelfManaged) return;
    const category = activeTab as SettingsCategory;
    setIsSaving(true);
    setMessage(null);
    try {
      const result = await api.put<{ data: Settings[SettingsCategory] }>(
        `/api/admin/settings/${category}`,
        settings[category],
      );
      setSettings((prev) => (prev ? { ...prev, [category]: result.data } : prev));
      setMessage({ type: "success", text: "Settings saved" });
      setHasChanges(false);
    } catch {
      setMessage({ type: "error", text: "Failed to save settings" });
    } finally {
      setIsSaving(false);
    }
  };

  const updateSetting = <T extends SettingsCategory>(
    category: T,
    key: keyof Settings[T],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
  ) => {
    setSettings((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [category]: { ...prev[category], [key]: value },
      };
    });
    setHasChanges(true);
  };

  const tabs = [
    { id: "company" as const, name: "Company", icon: Building2 },
    { id: "smtp" as const, name: "SMTP", icon: Server },
    { id: "notifications" as const, name: "Notifications", icon: Bell },
    { id: "booking" as const, name: "Bookings", icon: CreditCard },
    { id: "seo" as const, name: "SEO & Analytics", icon: BarChart3 },
    { id: "social" as const, name: "Social Media", icon: Share2 },
    { id: "security" as const, name: "Security", icon: Shield },
    { id: "ai" as const, name: "AI (ChatGPT)", icon: Bot },
    { id: "marketing" as const, name: "Marketing & Tracking", icon: Megaphone },
    { id: "finance" as const, name: "Finance", icon: Banknote },
    { id: "cache" as const, name: "Cache", icon: Database },
  ];

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-primary-600 h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        Failed to load settings. Please refresh the page.
      </div>
    );
  }

  const tabProps = { settings, updateSetting, setMessage };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="mt-1 text-gray-500 dark:text-gray-400">Manage website and system settings</p>
      </div>

      {message && (
        <div
          className={`mb-6 rounded-lg p-4 ${
            message.type === "success"
              ? "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400"
              : "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="rounded-xl bg-white shadow-sm dark:bg-gray-800">
        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          {/* Wrap (not horizontal-scroll) so every tab — incl. Finance/Cache — is always
              visible; an off-screen scroll hid the last tabs with no affordance. */}
          <nav className="flex flex-wrap gap-1 px-4 sm:gap-2 sm:px-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  if (hasChanges && !isSelfManaged) {
                    if (confirm("You have unsaved changes. Continue without saving?")) {
                      setActiveTab(tab.id);
                      setHasChanges(false);
                    }
                  } else {
                    setActiveTab(tab.id);
                  }
                }}
                className={`flex shrink-0 items-center gap-2 border-b-2 px-3 py-4 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600 dark:text-primary-400"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        <div className="p-6">
          <Suspense
            fallback={
              <div className="flex h-32 items-center justify-center">
                <Loader2 className="text-primary-600 h-6 w-6 animate-spin" />
              </div>
            }
          >
            {activeTab === "company" && <CompanyTab {...tabProps} />}
            {activeTab === "smtp" && <SmtpTab {...tabProps} />}
            {activeTab === "notifications" && <NotificationsTab {...tabProps} />}
            {activeTab === "booking" && <BookingTab {...tabProps} />}
            {activeTab === "seo" && <SeoTab {...tabProps} />}
            {activeTab === "social" && <SocialTab {...tabProps} />}
            {activeTab === "security" && <SecurityTab {...tabProps} />}
            {activeTab === "ai" && <AiTab {...tabProps} />}
            {activeTab === "marketing" && <MarketingTab {...tabProps} />}
            {activeTab === "finance" && <FinanceTab setMessage={setMessage} />}
            {activeTab === "cache" && <CacheTab setMessage={setMessage} />}
          </Suspense>
        </div>

        {/* Save button */}
        {!isSelfManaged && (
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            {hasChanges && (
              <span className="text-sm text-amber-600 dark:text-amber-400">
                You have unsaved changes
              </span>
            )}
            <div className="ml-auto">
              <button
                onClick={handleSave}
                disabled={isSaving || !hasChanges}
                className="bg-primary-600 hover:bg-primary-700 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
